import { useState, useMemo } from "react";
import { IconCheck, IconCopy } from "../";
import type { ParsedSection, FontSize } from "../../types/caseView";
import { getFontSizeClasses } from "../../utils/caseViewHelpers";

// Lifebuoy icon for panic button
function IconLifebuoy({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1.5}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M16.712 4.33a9.027 9.027 0 011.652 1.306c.51.51.944 1.064 1.306 1.652M16.712 4.33l-3.448 4.138m3.448-4.138a9.014 9.014 0 00-9.424 0M19.67 7.288l-4.138 3.448m4.138-3.448a9.014 9.014 0 010 9.424m-4.138-5.976a3.736 3.736 0 00-.88-1.388 3.737 3.737 0 00-1.388-.88m2.268 2.268a3.765 3.765 0 010 2.528m-2.268-4.796a3.765 3.765 0 00-2.528 0m4.796 4.796c-.181.506-.475.982-.88 1.388a3.736 3.736 0 01-1.388.88m2.268-2.268l4.138 3.448m0 0a9.027 9.027 0 01-1.306 1.652c-.51.51-1.064.944-1.652 1.306m0 0l-3.448-4.138m3.448 4.138a9.014 9.014 0 01-9.424 0m5.976-4.138a3.765 3.765 0 01-2.528 0m0 0a3.736 3.736 0 01-1.388-.88 3.737 3.737 0 01-.88-1.388m2.268 2.268L7.288 19.67m0 0a9.024 9.024 0 01-1.652-1.306 9.027 9.027 0 01-1.306-1.652m0 0l4.138-3.448M4.33 16.712a9.014 9.014 0 010-9.424m4.138 5.976a3.765 3.765 0 010-2.528m0 0c.181-.506.475-.982.88-1.388a3.736 3.736 0 011.388-.88m-2.268 2.268L4.33 7.288m6.406 1.18L7.288 4.33m0 0a9.024 9.024 0 00-1.652 1.306A9.025 9.025 0 004.33 7.288"
      />
    </svg>
  );
}

function IconX({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M6 18L18 6M6 6l12 12"
      />
    </svg>
  );
}

export function PanicButton({
  sections,
  callMode,
  fontSize,
}: {
  sections: ParsedSection[];
  callMode: boolean;
  fontSize: FontSize;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const fontClass = getFontSizeClasses(fontSize, callMode);

  const strongestArgument = useMemo(() => {
    const openingSection = sections.find(
      (s) =>
        s.title.toUpperCase().includes("OPENING") ||
        s.title.toUpperCase().includes("STATEMENT")
    );
    if (openingSection) {
      const lines = openingSection.content.split("\n").filter((l) => l.trim());
      return {
        title: "Your Opening Statement",
        content: lines.slice(0, 3).join("\n"),
        fullContent: openingSection.content,
      };
    }

    const talkingSection = sections.find(
      (s) =>
        s.iconType === "talking" || s.title.toUpperCase().includes("TALKING")
    );
    if (talkingSection) {
      const firstPoint =
        talkingSection.content.split(/\n\d+\./)[0] ||
        talkingSection.content.split("\n")[0];
      return {
        title: "Your Strongest Point",
        content: firstPoint
          .replace(/^\d+\.\s*/, "")
          .replace(/\*\*/g, "")
          .trim(),
        fullContent: talkingSection.content,
      };
    }

    if (sections.length > 0) {
      return {
        title: "Key Point",
        content: sections[0].content.split("\n")[0],
        fullContent: sections[0].content,
      };
    }

    return null;
  }, [sections]);

  const handleCopy = async () => {
    if (strongestArgument) {
      await navigator.clipboard.writeText(strongestArgument.content);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  if (!callMode || !strongestArgument) return null;

  return (
    <>
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-6 left-6 z-50 flex items-center gap-2 px-5 py-3 bg-gradient-to-r from-rose-500 to-rose-600 text-white rounded-full shadow-lg hover:from-rose-600 hover:to-rose-700 active:scale-95 transition-all duration-200 font-medium"
      >
        <IconLifebuoy className="w-5 h-5" />
        <span>Strongest Argument</span>
      </button>

      {isOpen && (
        <div
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
          onClick={() => setIsOpen(false)}
        >
          <div
            className="w-full max-w-lg bg-slate-800 rounded-2xl shadow-2xl border border-slate-700 overflow-hidden animate-in slide-in-from-bottom-4 duration-200"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-4 border-b border-slate-700 bg-gradient-to-r from-rose-500/10 to-transparent">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-rose-500/20 rounded-lg">
                  <IconLifebuoy className="w-5 h-5 text-rose-400" />
                </div>
                <div>
                  <h3 className="font-semibold text-white">
                    {strongestArgument.title}
                  </h3>
                  <p className="text-xs text-slate-400">
                    Your key point to make
                  </p>
                </div>
              </div>
              <button
                onClick={() => setIsOpen(false)}
                className="p-2 rounded-lg hover:bg-slate-700 text-slate-400 hover:text-white transition-colors"
              >
                <IconX className="w-5 h-5" />
              </button>
            </div>

            <div className="p-5">
              <div className="p-4 bg-slate-700/50 rounded-xl border border-slate-600">
                <p
                  className={`${fontClass} text-slate-100 leading-relaxed whitespace-pre-wrap`}
                >
                  {strongestArgument.content}
                </p>
              </div>
            </div>

            <div className="flex gap-3 p-4 border-t border-slate-700 bg-slate-800/80">
              <button
                onClick={handleCopy}
                className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-xl font-medium transition-all ${
                  copied
                    ? "bg-teal-500 text-white"
                    : "bg-slate-700 text-slate-200 hover:bg-slate-600"
                }`}
              >
                {copied ? (
                  <>
                    <IconCheck className="w-5 h-5" />
                    Copied!
                  </>
                ) : (
                  <>
                    <IconCopy className="w-5 h-5" />
                    Copy to Clipboard
                  </>
                )}
              </button>
              <button
                onClick={() => setIsOpen(false)}
                className="px-6 py-3 rounded-xl font-medium bg-rose-500 text-white hover:bg-rose-600 transition-colors"
              >
                Got it
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}